import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatAmount, formatDateTime } from '@/lib/format';
import { EWB_SUB_SUPPLY, useEwayBills, type EwayBillRow } from '@/lib/gst';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/taxes/gst/eway-bills')({ component: EwayBills });

const CHIPS: Array<[string, string]> = [
  ['active', 'Active'],
  ['expired', 'Expired'],
  ['cancelled', 'Cancelled'],
  ['all', 'All'],
];

const state = (r: EwayBillRow) =>
  r.status === 'cancelled' ? 'cancelled' : r.expired ? 'expired' : 'active';

/** Screen T11 (document 06): Taxes › GST › E-way bills, India clients. */
function EwayBills() {
  const orgId = useOrgId();
  const { taxPack, currency } = useOrgLocale();
  const q = useEwayBills();
  const [chip, setChip] = useState('active');
  const rows = (q.data ?? []).filter((r) => chip === 'all' || state(r) === chip);
  return (
    <>
      <PageHeader
        title="E-way bills"
        description="Generated from approved invoices and delivery challans; a bill is cancelled within 24 hours, or its vehicle updated while it is valid."
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : taxPack !== 'in' ? (
        <EmptyState title="E-way bills apply to India clients" />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Status">
            {CHIPS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={chip === k}
                onClick={() => setChip(k)}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  chip === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {rows.length === 0 ? (
            <EmptyState
              title="No e-way bills here"
              hint="Generate one from an approved invoice or a delivery challan."
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border bg-card">
              <Table data-testid="eway-bills">
                <TableHeader>
                  <TableRow>
                    <TableHead>EWB no.</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Document</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Valid until</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="tnum font-medium">{r.ewbNo}</TableCell>
                      <TableCell className="text-xs">{formatDateTime(r.ewbDate)}</TableCell>
                      <TableCell>
                        <Link
                          className="text-primary hover:underline"
                          to={`${
                            r.documentKind === 'delivery_challan'
                              ? '/sales/delivery-challans'
                              : '/sales/invoices'
                          }/${r.documentId}`}
                        >
                          {r.documentNumber}
                        </Link>
                      </TableCell>
                      <TableCell>{r.contactName}</TableCell>
                      <TableCell className="text-xs">
                        {EWB_SUB_SUPPLY[r.subSupplyType] ?? r.subSupplyType}
                      </TableCell>
                      <TableCell className="tnum text-right">
                        {currency} {formatAmount(r.total)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.validUntil ? formatDateTime(r.validUntil) : 'Part A only'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.vehicleNo ?? r.transDocNo ?? r.transporterName ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={state(r) === 'active' ? 'default' : 'secondary'}>
                          {state(r) === 'active'
                            ? 'Active'
                            : state(r) === 'expired'
                              ? 'Expired'
                              : 'Cancelled'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </>
  );
}
